import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import { STATS } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Candle = () => (
  <div className="flex flex-col items-center" aria-hidden="true">
    <div className="candle-flame flame" />
    <div className="w-[3px] h-3 bg-[#D4AF37] mt-0.5 opacity-80" />
    <div className="w-2 h-6 bg-[#1A1A1A] border border-white/10" />
  </div>
);

export const Memorial = () => {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/memorial-messages`);
      setMessages(res.data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      toast.error("Please share a name and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/memorial-messages`, {
        name: name.trim(),
        location: location.trim() || null,
        message: message.trim(),
      });
      setName("");
      setLocation("");
      setMessage("");
      toast.success("A candle has been lit in remembrance.");
      fetchMessages();
    } catch (e) {
      toast.error("We could not light your candle. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="memorial-page">
      <div className="overline text-[#D4AF37]">A place of remembrance</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EBE5] mt-4 max-w-3xl leading-tight">
        Light a candle. Leave a name. Say what must not be forgotten.
      </h1>

      {/* Memorial Wall stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5 mt-16" data-testid="memorial-stats">
        {STATS.map((s, i) => (
          <div key={i} className="bg-[#0A0A0C] p-6">
            <div className="font-serif-display text-2xl text-[#D4AF37]">{s.value}</div>
            <div className="overline mt-2">{s.label}</div>
            <div className="text-xs text-[#6E6B67] mt-3">{s.note}</div>
          </div>
        ))}
      </div>

      {/* Candles row */}
      <div className="mt-20 grid grid-cols-6 sm:grid-cols-10 md:grid-cols-14 gap-6" data-testid="memorial-candles">
        {Array.from({ length: 28 }).map((_, i) => (
          <Candle key={i} />
        ))}
      </div>
      <div className="text-center overline mt-6">A candle for every survivor who speaks.</div>

      {/* Leave a message */}
      <div className="grid lg:grid-cols-2 gap-16 mt-24">
        <form
          onSubmit={submit}
          className="border border-white/10 p-8 bg-[#111215]"
          data-testid="memorial-form"
        >
          <div className="overline mb-2 flex items-center gap-2">
            <Flame className="w-3 h-3 text-[#D4AF37] flame" strokeWidth={1.5} /> Leave a message
          </div>
          <h2 className="font-serif-display text-2xl text-[#F2EBE5]">In remembrance</h2>
          <p className="text-sm text-[#A19D98] mt-3 leading-relaxed">
            Share a few words for the victims, the survivors, or the still-missing.
          </p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="overline block mb-2" htmlFor="m-name">Your name</label>
              <input
                id="m-name"
                data-testid="memorial-input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="A first name is enough"
              />
            </div>
            <div>
              <label className="overline block mb-2" htmlFor="m-loc">Where you write from <span className="text-[#6E6B67]">(optional)</span></label>
              <input
                id="m-loc"
                data-testid="memorial-input-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                maxLength={80}
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="A city or a country"
              />
            </div>
            <div>
              <label className="overline block mb-2" htmlFor="m-msg">Your message</label>
              <textarea
                id="m-msg"
                data-testid="memorial-input-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={600}
                rows={5}
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                placeholder="A few words of remembrance, of solidarity, of hope."
              />
              <div className="text-[10px] text-[#6E6B67] mt-1 text-right">{message.length}/600</div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="memorial-submit"
              className="w-full px-6 py-3 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#E5C07B] transition-colors duration-500 disabled:opacity-60"
            >
              {loading ? "Lighting candle…" : "Light a candle"}
            </button>
          </div>
        </form>

        <div data-testid="memorial-list">
          <div className="overline mb-4">Recent candles ({messages.length})</div>
          <div className="space-y-6 max-h-[720px] overflow-y-auto pr-2">
            {messages.length === 0 && (
              <div className="text-sm text-[#6E6B67] italic">
                Be the first to light a candle.
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className="border border-white/10 p-6 bg-[#0A0A0C]"
                data-testid={`memorial-message-${m.id}`}
              >
                <div className="flex items-center gap-3">
                  <Flame className="w-4 h-4 text-[#D4AF37] flame" strokeWidth={1.5} />
                  <div className="text-[#F2EBE5] text-sm">{m.name}</div>
                  {m.location && (
                    <div className="overline">· {m.location}</div>
                  )}
                </div>
                <p className="text-[#A19D98] text-sm mt-4 leading-relaxed font-serif-display italic">
                  “{m.message}”
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Memorial;
